import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { KiteTabs, KiteLoader } from '@kite/react-kite';
import TCOOverview from 'components/TCOComponents/TCOOverview';

import {
  useAnalytics,
  useGetTcoLocations,
  useGetTcoSubmissions,
  useGetTcoUnderlayProducts,
  useQueryData,
  useUpdateTcoSubmissions,
} from 'hooks';
import { IEstimate, ITCOSection, ITCOForm } from 'types';
import {
  AutosaveBadge,
  TCOAcquisitionCostsWrapper,
  TCOImplementationCostsWrapper,
  TCOLocationsWrapper,
} from 'components';
import { tcoFormsDefaultValues } from 'utils';
import './TCOLayout.scss';
import TCOOperationalCostsWrapper from 'components/TCOComponents/TCOOperationalCostsTab/TCOOperationalCostsWrapper';
import TCODashboard from 'components/TCOComponents/TCODashboard';

export interface ITCOLayoutProps {
  estimate?: IEstimate;
  sections?: ITCOSection[];
  forms?: ITCOForm[];
}

const TCOLayout = ({
  estimate,
  sections = [],
  forms = [],
}: ITCOLayoutProps) => {
  const { userId } = useQueryData();
  // =============================================
  // State/Refs/Hooks
  // =============================================
  const { trackSelectAction } = useAnalytics();
  const [currentTab, setCurrentTab] = useState('Acquisition Costs');

  // =============================================
  // Constants
  // =============================================

  const tabOptions = useMemo(
    () => Array.from(new Set(sections.map((section) => section.sectionName))),
    [sections]
  );
  // =============================================
  // Interaction Handlers
  // =============================================
  const handleTabChange = useCallback(
    (value: string) => {
      setCurrentTab(value);
      trackSelectAction(`TCO Select Tab: ${value}`, {
        opType: 'buttonClick',
      });
    },
    [trackSelectAction]
  );

  const { estimateId } = useParams();

  const currentSectionId = useMemo(
    () => sections?.find((f) => f.sectionName === currentTab)?.id || '',
    [sections, currentTab]
  );

  // Return the forms based on selected section
  const currentForms = useMemo(
    () => forms?.filter((form) => form?.sectionId === currentSectionId),
    [currentSectionId, forms]
  );

  // Get all the form submissions for given estimate ID
  const { data: tcoSubmissions = [], isLoading: submissionsLoading } =
    useGetTcoSubmissions({
      params: { estimateId },
    });

  const { updateTcoSubmissions } = useUpdateTcoSubmissions({
    estimateId: estimateId || '',
  });

  const { isLoading: tcoLocationsLoading } = useGetTcoLocations({
    params: { estimateId },
  });

  const { isLoading: tcoUnderlayProductsLoading } = useGetTcoUnderlayProducts();

  useEffect(() => {
    // Post the default form values for TCO if accessed for the first time or if any of the default form fields is unanswered
    if (
      !submissionsLoading &&
      !tcoFormsDefaultValues.every((d) =>
        tcoSubmissions.find((s) => s.fieldId === d.fieldId)
      )
    ) {
      const tcoFormsDefaultData = tcoFormsDefaultValues.map((defaultData) => {
        let submittedAnswer = tcoSubmissions.find(
          (s) => s.fieldId === defaultData.fieldId
        )?.answer;
        return {
          ...defaultData,
          answer: submittedAnswer ?? defaultData.answer, // use submitted answer if exist else default answer
          estimateId,
          updatedBy: userId,
        };
      });
      updateTcoSubmissions(tcoFormsDefaultData);
    }
  }, [
    forms,
    tcoSubmissions,
    updateTcoSubmissions,
    submissionsLoading,
    userId,
    estimateId,
  ]);

  const isLoading =
    submissionsLoading || tcoLocationsLoading || tcoUnderlayProductsLoading;

  // =============================================
  // Return
  // =============================================
  return (
    <div className="tco-layout">
      <div className="tco-layout__upper">
        <div className="tco-layout__header">
          <span className="tco-layout__estimate-name">
            {estimate?.name || 'No Name Provided'}
          </span>
          <div className="tco-badge-section">
            <h1>Total Cost of Ownership</h1>
            <AutosaveBadge />
          </div>
        </div>
        <div className="tco-layout__upper-sub">
          <KiteTabs
            tabs={tabOptions}
            currentTab={currentTab}
            onSelectTab={handleTabChange}
          />
        </div>
      </div>
      <div className="tco-layout__content">
        {isLoading && <KiteLoader className="tco-layout__loader" />}
        <div
          className={`tco-layout__tab-content ${
            currentTab === 'Dashboard' ? 'grid-col-max' : ''
          }`}
        >
          {currentTab === 'Acquisition Costs' && (
            <div className="tco-layout__form-content">
              {currentForms && tcoSubmissions && (
                <TCOAcquisitionCostsWrapper
                  currentForms={currentForms}
                  tcoSubmissions={tcoSubmissions}
                />
              )}
            </div>
          )}
          {currentTab === 'Implementation Costs' && (
            <div className="tco-layout__form-content">
              {currentForms && tcoSubmissions && (
                <TCOImplementationCostsWrapper
                  currentForms={currentForms}
                  tcoSubmissions={tcoSubmissions}
                />
              )}
            </div>
          )}
          {currentTab === 'Operational Costs' && (
            <div>
              {currentForms && tcoSubmissions && (
                <TCOOperationalCostsWrapper
                  currentForms={currentForms}
                  tcoSubmissions={tcoSubmissions}
                />
              )}
            </div>
          )}
          {currentTab === 'Locations' && (
            <div>
              {currentForms && tcoSubmissions && (
                <TCOLocationsWrapper
                  currentForms={currentForms}
                  tcoSubmissions={tcoSubmissions}
                />
              )}
            </div>
          )}
          {currentTab === 'RingCentral' && (
            <div style={{ paddingTop: '100px', textAlign: 'center' }}>
              <h1>Voice Coming Soon</h1>
            </div>
          )}
          {currentTab === 'Dashboard' && (
            <div>
              {tcoSubmissions && (
                <TCODashboard tcoSubmissions={tcoSubmissions || []} />
              )}
            </div>
          )}
        </div>

        {currentTab !== 'Dashboard' && (
          <div className="tco-layout__overview-section">
            <TCOOverview tcoSubmissions={tcoSubmissions || []} />
          </div>
        )}
      </div>
    </div>
  );
};

export default TCOLayout;
